/**
 * eScribe Security - Client-side bot protection utilities
 *
 * Include in forms that need bot protection:
 * - Device fingerprinting
 * - Proof-of-Work solver
 * - User interaction tracking
 * - Honeypot field handling
 * - Form timing
 */

// ============================================
// DEVICE FINGERPRINTING
// ============================================

/**
 * Collect device fingerprint signals
 */
export const collectFingerprint = async () => {
  const signals = {};

  try {
    // Screen properties
    signals.screenResolution = `${window.screen.width}x${window.screen.height}`;
    signals.screenColorDepth = window.screen.colorDepth;
    signals.screenPixelRatio = window.devicePixelRatio || 1;
    signals.availableResolution = `${window.screen.availWidth}x${window.screen.availHeight}`;

    // Timezone
    signals.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    signals.timezoneOffset = new Date().getTimezoneOffset();

    // Language
    signals.language = navigator.language;
    signals.languages = navigator.languages?.join(",") || navigator.language;

    // Platform - use User-Agent Client Hints API with fallback
    try {
      if (navigator.userAgentData) {
        signals.platform = navigator.userAgentData.platform || "unknown";
        signals.mobile = navigator.userAgentData.mobile;
        signals.brands = navigator.userAgentData.brands
          ?.map((b) => `${b.brand}/${b.version}`)
          .join(",");
      } else {
        // Fallback: extract from userAgent string
        const ua = navigator.userAgent;
        if (ua.includes("Win")) signals.platform = "Windows";
        else if (ua.includes("Mac")) signals.platform = "macOS";
        else if (ua.includes("Linux")) signals.platform = "Linux";
        else if (ua.includes("Android")) signals.platform = "Android";
        else if (ua.includes("iPhone") || ua.includes("iPad")) {
          signals.platform = "iOS";
        } else signals.platform = "unknown";
      }
    } catch {
      signals.platform = "unknown";
    }
    signals.userAgent = navigator.userAgent;

    // Hardware
    signals.hardwareConcurrency = navigator.hardwareConcurrency;
    signals.deviceMemory = navigator.deviceMemory;
    signals.maxTouchPoints = navigator.maxTouchPoints;

    // Browser features
    signals.cookiesEnabled = navigator.cookieEnabled;
    signals.doNotTrack = navigator.doNotTrack;
    signals.pdfViewerEnabled = navigator.pdfViewerEnabled;

    // Automation detection
    signals.webdriver = navigator.webdriver || false;
    signals.automationControlled = !!(
      window.document.__selenium_unwrapped ||
      window.document.__webdriver_evaluate ||
      window.document.__driver_evaluate ||
      window.navigator.webdriver ||
      window.callPhantom ||
      window._phantom ||
      window.__nightmare ||
      window.domAutomation ||
      window.domAutomationController
    );

    // Canvas fingerprint
    signals.canvasHash = await getCanvasFingerprint();

    // WebGL fingerprint
    signals.webglVendor = getWebGLInfo().vendor;
    signals.webglRenderer = getWebGLInfo().renderer;

    // Audio fingerprint
    signals.audioFingerprint = await getAudioFingerprint();

    // Font detection (sample)
    signals.fontList = detectFonts();

    // Plugin list
    signals.plugins = getPlugins();

    // Storage availability
    signals.localStorage = hasLocalStorage();
    signals.sessionStorage = hasSessionStorage();
    signals.indexedDB = hasIndexedDB();

    // Connection info
    if (navigator.connection) {
      signals.connectionType = navigator.connection.effectiveType;
      signals.connectionDownlink = navigator.connection.downlink;
    }
  } catch (error) {
    console.warn("Fingerprint collection error:", error);
  }
  return signals;
};

/**
 * Generate canvas fingerprint
 */
const getCanvasFingerprint = async () => {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 200;
    canvas.height = 50;

    // Draw text with specific styling
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("eScribe Security", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("eScribe Security", 4, 17);

    // Get data URL and hash
    const dataUrl = canvas.toDataURL();
    return await hashString(dataUrl);
  } catch {
    return null;
  }
};

/**
 * Get WebGL vendor and renderer
 */
const getWebGLInfo = () => {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

    if (!gl) return { vendor: null, renderer: null };

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");

    return {
      vendor: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : null,
      renderer: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : null,
    };
  } catch {
    return { vendor: null, renderer: null };
  }
};

/**
 * Get audio fingerprint using OfflineAudioContext
 */
const getAudioFingerprint = async () => {
  try {
    const AudioContext =
      window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!AudioContext) return null;

    // Create offline context - 1 channel, 5000 samples, 44100 sample rate
    const context = new AudioContext(1, 5000, 44100);

    // Create oscillator
    const oscillator = context.createOscillator();
    oscillator.type = "triangle";
    oscillator.frequency.value = 1000;

    // Create compressor for more unique fingerprint
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;

    // Connect: oscillator -> compressor -> destination
    oscillator.connect(compressor);
    compressor.connect(context.destination);

    // Start oscillator
    oscillator.start(0);

    // Render audio with timeout
    const fingerprint = await Promise.race([
      context.startRendering().then((buffer) => {
        const data = buffer.getChannelData(0);
        let sum = 0;
        for (let i = 4500; i < 5000; i++) {
          sum += Math.abs(data[i]);
        }
        return sum.toFixed(6);
      }),
      new Promise((resolve) => setTimeout(() => resolve(null), 1000)),
    ]);

    return fingerprint;
  } catch (e) {
    console.warn("Audio fingerprint error:", e);
    return null;
  }
};

/**
 * Detect available fonts (sample list)
 */
const detectFonts = () => {
  const baseFonts = ["monospace", "sans-serif", "serif"];
  const testFonts = [
    "Arial",
    "Arial Black",
    "Comic Sans MS",
    "Courier New",
    "Georgia",
    "Impact",
    "Times New Roman",
    "Trebuchet MS",
    "Verdana",
    "Helvetica",
  ];

  const detected = [];
  const testString = "mmmmmmmmmmlli";
  const testSize = "72px";

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const getWidth = (fontFamily) => {
    ctx.font = `${testSize} ${fontFamily}`;
    return ctx.measureText(testString).width;
  };

  const baseWidths = baseFonts.map(getWidth);

  for (const font of testFonts) {
    for (let i = 0; i < baseFonts.length; i++) {
      const width = getWidth(`'${font}', ${baseFonts[i]}`);
      if (width !== baseWidths[i]) {
        detected.push(font);
        break;
      }
    }
  }

  return detected.join(",");
};

/**
 * Get browser plugins
 */
const getPlugins = () => {
  if (!navigator.plugins) return "";

  const plugins = [];
  for (let i = 0; i < navigator.plugins.length; i++) {
    plugins.push(navigator.plugins[i].name);
  }
  return plugins.sort().join(",");
};

/**
 * Check storage availability
 */
const hasLocalStorage = () => {
  try {
    localStorage.setItem("test", "test");
    localStorage.removeItem("test");
    return true;
  } catch {
    return false;
  }
};

const hasSessionStorage = () => {
  try {
    sessionStorage.setItem("test", "test");
    sessionStorage.removeItem("test");
    return true;
  } catch {
    return false;
  }
};

const hasIndexedDB = () => !!window.indexedDB;

/**
 * Hash a string using SHA-256
 */
const hashString = async (str) => {
  const msgBuffer = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Encode fingerprint for transmission
 */
export const encodeFingerprint = async () => {
  const signals = await collectFingerprint();
  return btoa(JSON.stringify(signals));
};

// ============================================
// PROOF OF WORK SOLVER
// ============================================

/**
 * Solve a Proof-of-Work challenge
 */
export const solveProofOfWork = async (challenge, difficulty, onProgress) => {
  const startTime = Date.now();
  const requiredPrefix = "0".repeat(difficulty);
  let nonce = 0;
  let hash = "";

  // Use Web Worker for heavy computation
  if (window.Worker) {
    return new Promise((resolve, reject) => {
      const workerCode = `
        self.onmessage = async function(e) {
          const { challenge, difficulty } = e.data;
          const requiredPrefix = '0'.repeat(difficulty);
          let nonce = 0;

          while (true) {
            const data = challenge + nonce;
            const msgBuffer = new TextEncoder().encode(data);
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

            if (hash.startsWith(requiredPrefix)) {
              self.postMessage({ nonce, hash, attempts: nonce });
              return;
            }

            nonce++;

            if (nonce % 10000 === 0) {
              self.postMessage({ progress: nonce });
            }

            if (nonce > 10000000) {
              self.postMessage({ error: 'Max attempts exceeded' });
              return;
            }
          }
        };
      `;

      const blob = new Blob([workerCode], { type: "application/javascript" });
      const worker = new Worker(URL.createObjectURL(blob));

      worker.onmessage = (e) => {
        if (e.data.error) {
          reject(new Error(e.data.error));
          worker.terminate();
        } else if (e.data.progress) {
          onProgress?.(e.data.progress);
        } else {
          resolve({
            nonce: e.data.nonce,
            hash: e.data.hash,
            timeTaken: Date.now() - startTime,
            attempts: e.data.attempts,
          });
          worker.terminate();
        }
      };

      worker.onerror = (e) => {
        reject(e);
        worker.terminate();
      };

      worker.postMessage({ challenge, difficulty });
    });
  }

  // Fallback for browsers without Worker support
  while (nonce < 10000000) {
    const data = challenge + nonce;
    const msgBuffer = new TextEncoder().encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    hash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

    if (hash.startsWith(requiredPrefix)) {
      return {
        nonce,
        hash,
        timeTaken: Date.now() - startTime,
        attempts: nonce,
      };
    }

    nonce++;

    if (nonce % 10000 === 0) {
      onProgress?.(nonce);
      // Yield to event loop
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  throw new Error("Could not solve challenge");
};

// ============================================
// USER INTERACTION TRACKING
// ============================================

/**
 * Track user interactions on a page
 */
export class InteractionTracker {
  constructor() {
    this.mouseMovements = 0;
    this.keystrokes = 0;
    this.scrollEvents = 0;
    this.clicks = 0;
    this.touchEvents = 0;
    this.lastMousePosition = null;
    this.mouseTrajectory = [];
    this.keystrokeTimings = [];
    this.lastKeystrokeTime = null;

    this.startTracking();
  }

  startTracking() {
    // Mouse movement
    document.addEventListener("mousemove", (e) => {
      this.mouseMovements++;

      // Track trajectory (sample every 10th movement)
      if (this.mouseMovements % 10 === 0) {
        this.mouseTrajectory.push({
          x: e.clientX,
          y: e.clientY,
          t: Date.now(),
        });

        // Keep only last 100 points
        if (this.mouseTrajectory.length > 100) {
          this.mouseTrajectory.shift();
        }
      }

      this.lastMousePosition = { x: e.clientX, y: e.clientY };
    });

    // Keystrokes
    document.addEventListener("keydown", () => {
      this.keystrokes++;

      const now = Date.now();
      if (this.lastKeystrokeTime) {
        const interval = now - this.lastKeystrokeTime;
        this.keystrokeTimings.push(interval);

        // Keep only last 50 timings
        if (this.keystrokeTimings.length > 50) {
          this.keystrokeTimings.shift();
        }
      }
      this.lastKeystrokeTime = now;
    });

    // Scroll
    document.addEventListener("scroll", () => {
      this.scrollEvents++;
    });

    // Click
    document.addEventListener("click", () => {
      this.clicks++;
    });

    // Touch
    document.addEventListener("touchstart", () => {
      this.touchEvents++;
    });
  }

  /**
   * Get interaction summary
   */
  getSummary() {
    return {
      mouseMovements: this.mouseMovements,
      keystrokes: this.keystrokes,
      scrollEvents: this.scrollEvents,
      clicks: this.clicks,
      touchEvents: this.touchEvents,
      avgKeystrokeInterval: this.getAverageKeystrokeInterval(),
      mouseVelocity: this.getMouseVelocity(),
      trajectoryLinearity: this.getTrajectoryLinearity(),
    };
  }

  /**
   * Get average keystroke interval
   */
  getAverageKeystrokeInterval() {
    if (this.keystrokeTimings.length === 0) return null;
    const sum = this.keystrokeTimings.reduce((a, b) => a + b, 0);
    return sum / this.keystrokeTimings.length;
  }

  /**
   * Get average mouse velocity
   */
  getMouseVelocity() {
    if (this.mouseTrajectory.length < 2) return null;

    let totalDistance = 0;
    let totalTime = 0;

    for (let i = 1; i < this.mouseTrajectory.length; i++) {
      const prev = this.mouseTrajectory[i - 1];
      const curr = this.mouseTrajectory[i];

      const distance = Math.sqrt(
        Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2),
      );
      const time = curr.t - prev.t;

      totalDistance += distance;
      totalTime += time;
    }

    return totalTime > 0 ? totalDistance / totalTime : null;
  }

  /**
   * Check if mouse trajectory is too linear (bot-like)
   */
  getTrajectoryLinearity() {
    if (this.mouseTrajectory.length < 10) return null;

    // Calculate how "straight" the path is
    const first = this.mouseTrajectory[0];
    const last = this.mouseTrajectory[this.mouseTrajectory.length - 1];

    const directDistance = Math.sqrt(
      Math.pow(last.x - first.x, 2) + Math.pow(last.y - first.y, 2),
    );

    let actualDistance = 0;
    for (let i = 1; i < this.mouseTrajectory.length; i++) {
      const prev = this.mouseTrajectory[i - 1];
      const curr = this.mouseTrajectory[i];
      actualDistance += Math.sqrt(
        Math.pow(curr.x - prev.x, 2) + Math.pow(curr.y - prev.y, 2),
      );
    }

    // Ratio close to 1 means very linear (suspicious)
    return actualDistance > 0 ? directDistance / actualDistance : null;
  }

  /**
   * Encode for transmission
   */
  encode() {
    return JSON.stringify(this.getSummary());
  }
}

// ============================================
// FORM SECURITY HELPERS
// ============================================

/**
 * Initialize form security
 */
export const initFormSecurity = (formElement) => {
  const tracker = new InteractionTracker();
  const formLoadedAt = Date.now();

  // Add hidden timestamp field
  const timestampField = document.createElement("input");
  timestampField.type = "hidden";
  timestampField.name = "_formLoadedAt";
  timestampField.value = formLoadedAt.toString();
  formElement.appendChild(timestampField);

  // Add honeypot fields (hidden via CSS, not type="hidden")
  const honeypots = ["website", "phone2", "fax"];
  honeypots.forEach((name) => {
    const honeypot = document.createElement("input");
    honeypot.type = "text";
    honeypot.name = name;
    honeypot.tabIndex = -1;
    honeypot.autocomplete = "off";
    honeypot.style.cssText =
      "position:absolute;left:-9999px;top:-9999px;opacity:0;pointer-events:none;";
    honeypot.setAttribute("aria-hidden", "true");
    formElement.appendChild(honeypot);
  });

  // Intercept form submission
  formElement.addEventListener("submit", async () => {
    // Add interaction data
    const interactionsField = document.createElement("input");
    interactionsField.type = "hidden";
    interactionsField.name = "_interactions";
    interactionsField.value = tracker.encode();
    formElement.appendChild(interactionsField);

    // Add fingerprint
    const fingerprint = await encodeFingerprint();
    const fpField = document.createElement("input");
    fpField.type = "hidden";
    fpField.name = "_fingerprint";
    fpField.value = fingerprint;
    formElement.appendChild(fpField);
  });

  return {
    tracker,
    formLoadedAt,
    getFingerprint: encodeFingerprint,
  };
};

/**
 * Create security headers for fetch requests
 */
export const getSecurityHeaders = async (tracker) => {
  const fingerprint = await encodeFingerprint();

  return {
    "X-Device-Fingerprint": fingerprint,
    "X-Form-Loaded-At": Date.now().toString(),
    "X-User-Interactions": tracker?.encode() || "{}",
  };
};

// ============================================
// TURNSTILE INTEGRATION
// ============================================

/**
 * Load Cloudflare Turnstile widget
 */
export const loadTurnstile = (siteKey, containerId, callback) => {
  // Load Turnstile script if not already loaded
  if (!window.turnstile) {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      renderTurnstile(siteKey, containerId, callback);
    };
  } else {
    renderTurnstile(siteKey, containerId, callback);
  }
};

const renderTurnstile = (siteKey, containerId, callback) => {
  window.turnstile.render(`#${containerId}`, {
    sitekey: siteKey,
    callback,
    "error-callback": () => {
      console.error("Turnstile error");
    },
    "expired-callback": () => {
      console.warn("Turnstile token expired");
    },
  });
};

// ============================================
// EXPORTS
// ============================================

export default {
  collectFingerprint,
  encodeFingerprint,
  solveProofOfWork,
  InteractionTracker,
  initFormSecurity,
  getSecurityHeaders,
  loadTurnstile,
};
